import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryRefetchInterval } from '@cast/shared-types';
import { ProblematicNodesResponse } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const NODES_WITH_ISSUES_QUERY_KEY = 'nodes-with-issues';
const getProblematicNodesQueryKey = (params: {
  clusterId: string | undefined;
}) => [NODES_WITH_ISSUES_QUERY_KEY, params.clusterId] as const;

const getProblematicNodesQueryOptions = ({
  clusterId,
  refetchInterval,
  enabled = true,
}: {
  clusterId: string | undefined;
  refetchInterval?: QueryRefetchInterval<ProblematicNodesResponse>;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getProblematicNodesQueryKey({ clusterId }),
    queryFn: async () => {
      const { data } = await apiClient.autoscaler.getProblematicNodes({
        clusterId: clusterId!,
      });

      return data;
    },
    refetchInterval,
    enabled: makeIsEnabled(!!clusterId, enabled),
  });

export const useProblematicNodesQuery = (
  clusterId?: string,
  {
    refetchInterval = 20_000,
    enabled = true,
  }: {
    refetchInterval?: QueryRefetchInterval<ProblematicNodesResponse>;
    enabled?: boolean;
  } = {}
) => {
  const { isLoading, data, error, refetch } = useQuery(
    getProblematicNodesQueryOptions({
      clusterId,
      refetchInterval,
      enabled,
    })
  );

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};
